import api from "../lib/api";

/* ---------- Types ---------- */
export type MetaOption = string | { label: string; value: string };

export type FundingAmountOption = {
  label: string;
  value: string;
  min: number | null;
  max: number | null;
};

function normalizeMetaArray(raw: any): MetaOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item: any) => {
    if (typeof item === "string") return item;
    if (item && typeof item === "object") {
      return {
        label: item.label ?? item.value ?? "",
        value: item.value ?? item.label ?? "",
      };
    }
    return "";
  });
}

export type DiscoveryGrant = {
  id: string | number;
  title: string;
  description: string | null;
  url: string | null;
  status: string | null;
  open_date: string | null;
  close_date: string | null;
  total_fund_amount: string | null;
  min_award: number | null;
  max_award: number | null;
  funder: string | null;
  applicant_type: string | null;
  location: string | null;
  source_name: string | null;
  last_seen_date: string | null;
  is_active: boolean;
  updated_at: string;
  [k: string]: any;
};

export type DiscoverySearchResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: DiscoveryGrant[];
};

export type DeadlineOption = {
  label: string;
  value: string;
  start: string | null;
  end: string | null;
};

export type DiscoveryGroupedResponse = {
  active_grants?: DiscoveryGrant[];
  past_applications?: DiscoveryGrant[];
};

/* ---------- Calls ---------- */

export async function searchDiscovery(params: Record<string, any> = {}) {
  const { data } = await api.get<DiscoverySearchResponse>("/discovery/search/", { params });
  if (Array.isArray((data as any)?.results)) return data;
  return {
    count: (data as any)?.count ?? ((data as any)?.results?.length ?? 0),
    next: (data as any)?.next ?? null,
    previous: (data as any)?.previous ?? null,
    results: Array.isArray((data as any)?.results) ? (data as any).results : (Array.isArray(data) ? data : []),
  } as DiscoverySearchResponse;
}

export async function aiDiscovery(body: Record<string, any> = {}) {
  const { data } = await api.post<any>("/discovery/ai-discovery/", body);
  const results: DiscoveryGrant[] = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
  return results;
}

export async function aiSearchDiscoverList() {
  const { data } = await api.get("/ragrecommend/recommend-flex/?rerank_model=bge-reranker-v2-m3&use_profile=true");
  return data;
}

export type SaveDiscoveryResponse = {
  id?: number;
  saved_grant_id?: number;
  message?: string;
};

export async function saveDiscovery(payload: { grant_id?: string | number; grant?: any }) {
  const { data } = await api.post<SaveDiscoveryResponse>("/discovery/save/", payload);
  return data;
}

function normalizeStringList(data: any): string[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") return item.label ?? item.name ?? item.value ?? item.title ?? item.text ?? "";
      return "";
    })
    .filter(Boolean);
}

const withSelectAll = (list: string[]) => {
  const cleaned = list.filter((v) => v && v !== "Any");
  return ["Select All", ...cleaned];
};

export async function getFundingAmountsMeta(params: Record<string, any> = {}): Promise<FundingAmountOption[]> {
  const { data } = await api.get("/discovery/meta/funding-amounts/", { params });
  const arr: any[] = Array.isArray(data) ? data : (Array.isArray((data as any)?.results) ? (data as any).results : []);

  const opts: FundingAmountOption[] = arr.map((x) => ({
    label: String(x?.label ?? x?.value ?? ""),
    value: String(x?.value ?? x?.label ?? ""),
    min: x?.min === null || x?.min === undefined ? null : Number(x.min),
    max: x?.max === null || x?.max === undefined ? null : Number(x.max),
  }));

  const filtered = opts.filter((o) => o.label.toLowerCase() !== "any" && o.value.toLowerCase() !== "any");
  return [{ label: "Any", value: "Any", min: null, max: null }, ...filtered];
}

export async function getDeadlinesMeta(params: Record<string, any> = {}): Promise<DeadlineOption[]> {
  const { data } = await api.get("/discovery/meta/deadlines/", { params });
  const arr: any[] = Array.isArray(data) ? data : (Array.isArray((data as any)?.results) ? (data as any).results : []);

  const opts: DeadlineOption[] = arr.map((item) => ({
    label: String(item?.label ?? item?.value ?? ""),
    value: String(item?.value ?? item?.label ?? ""),
    start: item?.start ?? null,
    end: item?.end ?? null,
  }));

  const filtered = opts.filter((o) => o.label.toLowerCase() !== "any" && o.value.toLowerCase() !== "any");
  return [{ label: "Any", value: "Any", start: null, end: null }, ...filtered];
}

export async function getLocationsMeta(params: Record<string, any> = {}): Promise<string[]> {
  const { data } = await api.get("/discovery/meta/locations/", { params });
  return withSelectAll(normalizeStringList(data));
}

export async function getFunderTypesMeta(params: Record<string, any> = {}): Promise<string[]> {
  const { data } = await api.get("/discovery/meta/funder-types/", { params });
  return withSelectAll(normalizeStringList(data));
}

export async function deleteSavedGrant(saved_grant_id: number | string) {
  const url = `/organizer/saved-grants/${saved_grant_id}/`;
  const { data } = await api.delete(url, { data: { saved_grant_id } });
  return data;
}

export async function getApplicantTypesMeta(params: Record<string, any> = {}): Promise<string[]> {
  const { data } = await api.get("/discovery/meta/sectors/", { params });
  return withSelectAll(normalizeStringList(data));
}

export async function getNormalizedTownsMeta(params: Record<string, any> = {}): Promise<string[]> {
  const { data } = await api.get("/discovery/meta/normalized-towns/", { params });
  return withSelectAll(normalizeStringList(data));
}

export async function getNormalizedCitiesMeta(params: Record<string, any> = {}): Promise<string[]> {
  const { data } = await api.get("/discovery/meta/normalized-cities/", { params });
  return withSelectAll(normalizeStringList(data));
}

export async function getNormalizedCountriesMeta(params: Record<string, any> = {}): Promise<string[]> {
  const { data } = await api.get("/discovery/meta/normalized-countries/", { params });
  return withSelectAll(normalizeStringList(data));
}

export async function postGrantFullDetails(grant_id: string | undefined) {
  const { data } = await api.post(`/discovery/grant/detail/`, { grant_id });
  return data;
}

export async function getSectorsMeta(params: Record<string, any> = {}): Promise<MetaOption[]> {
  const res = await api.get("/discovery/meta/sectors/", { params });
  const list = normalizeMetaArray(res.data)
    .filter(Boolean)
    .filter((o) => {
      const lbl = typeof o === "string" ? o : (o.label ?? o.value ?? "");
      return lbl.trim().toLowerCase() !== "any";
    });

  const hasSelectAll = list.some((o) => {
    const lbl = typeof o === "string" ? o : (o.label ?? o.value ?? "");
    return lbl.trim().toLowerCase() === "select all";
  });

  return hasSelectAll ? list : ["Select All", ...list];
}

export type OrderingOption = { label: string; value: string };
export async function getOrderingOptionsMeta(): Promise<OrderingOption[]> {
  const { data } = await api.get("/discovery/meta/ordering-options/");
  const raw = Array.isArray(data) ? data : Array.isArray((data as any)?.results) ? (data as any).results : [];

  const options: OrderingOption[] = raw
    .map((x: any) =>
      typeof x === "string"
        ? { label: x, value: x }
        : { label: String(x?.label ?? x?.name ?? x?.value ?? ""), value: String(x?.value ?? x?.key ?? x?.label ?? "") }
    )
    .filter((o: OrderingOption) => o.label !== "");

  if (!options.some((o) => o.label.toLowerCase() === "relevance")) options.unshift({ label: "Relevance", value: "" });
  return options;
}
